import { useState } from 'react';
import { useSlate } from 'slate-react';
import { add } from 'date-fns'
import { Transforms, Editor } from 'slate';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'components/Shared/Toast'
import { useHistory } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import produce from 'immer';

import {
    useEditor,
    REMOVE_SIGNATURE,
    UPDATE_DOCUMENT,
    SET_CURRENT_ELEMENT,
    SET_PARTICIPANTS,
    UPDATE_SIGNATURE,
    ADD_SIGNATURE,
    ADD_PARTICIPANT,
    UPDATE_PARTICIPANT,
    SET_SIDEBAR_OPEN,
    REMOVE_PARTICIPANT,
    REMOVE_ELEMENT,
    NEXT_PAGE,
    PREV_PAGE,
    SET_TOTAL_PAGES,
    SET_CURRENT_PAGE,
    SET_CURRENT_SIDEBAR_TAB,
    SET_INIT_WORKFLOW_IN_PROGRESS,
    UPDATE_ELEMENTS,
    CHANGE_WORD,
    SET_PREVIEW_ONLY,
    SET_IS_FETCHING,
    ADD_VARIABLE,
    UPDATE_VARIABLE,
    REMOVE_VARIABLE,
    SET_IS_MIGRATING,
    CHANGE_ELEMENT_VERSION,
} from '../editor-context'

import { fetcher } from 'api/client'
import bootbox from 'components/Modals/Bootbox'
import { contractAction, updateContract, sendNotification as sendNotificationAction, clientContractAction, compareElements } from 'api/actions/contracts'
import { templateAction, updateTemplate, calculateQuestionsVariables } from 'api/actions/templates'
import { sendSpecialApproval, delegateGroup } from 'api/actions/collaboration'
import { useProfileState } from 'context/profile'
import useWhoami from 'hooks/useWhoami'
import { noop, getMongoObjectId, format, downloadFile, getApiName } from 'utils'
import { removeNodesById, removeNodesByProperty, getNodesById, migrateElements, updateVariable as updateEditorVariable } from '../Canvas/TratoFormat'
import useSidebarMenuOptions from '../Sidebar/useSidebarMenuOptions'

function getDocument(data) {
    return data.contract || data.template
}

const formatTagValue = (type, value) => {
    switch (type) {
      case 'text':
      case 'date':
        return value;
      case 'number':
      case 'currency':
        value = String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        if (type === 'currency') value = `$${value}`;
        return value;
      default: break;
    }
}

export const parseFormData = (doc = {}, data = {}, profile) => {
    const signatureType = data.signatureType;
    const blockchainType = data.blockchain;
    const documentType = data.documentType;
    const language = doc.language?.toLowerCase?.() ?? 'es';
    const parsedData = {}
  
    if (blockchainType) {
      parsedData.blockchain = {
        type: blockchainType.type || blockchainType,
        active: signatureType !== 'none' && blockchainType !== 'none'
      };
    } 
    if (signatureType) {
      parsedData.isPagare = ['certificate', 'autograph'].includes(signatureType) ? data.isPagare : false
    } 
    if (documentType?.value) {
      parsedData.type = documentType.value;
    } 
    if (data.participants) {
        const docParticipants = doc.participants
        parsedData.participants = produce(data.participants, (draft) => {
            return draft.map((part, i) => {
                const newPart = {
                    ...docParticipants[i],
                    ...part,
                    agendaProperties: {
                        ...(docParticipants.find(p => p._id === part._id)?.agendaProperties || {}),
                        ...(part?.agendaProperties || {})
                    },
                    userIsParticipant: part.userIsParticipant ? profile._id : undefined,
                }

                if (!Object.keys(newPart?.agendaProperties).length) delete newPart.agendaProperties;
                if (!newPart.requireGroupValidation) delete newPart.requireGroupValidation;

                return newPart
            })
        })
    }
  
    if (data.previewVariables) {
        const list = parsedData.participants ? parsedData.participants : doc.participants;

        parsedData.participants = list.map(p => {
            const variablesToUpdate = data.previewVariables[p._id];
            
            if (!variablesToUpdate) return p;
            return {
                ...p,
                variables: produce(p.variables, (draft) => {
                    draft.forEach((variable) => {
              
                        if (variablesToUpdate[variable._id]) {
                            if (variable.type === 'date') {
                                variable.dateValue = variablesToUpdate[variable._id].dateValue;
                                variable.value = format(variablesToUpdate[variable._id].dateValue, 'PPP', { locale: language });
                            } else {
                                variable.value = variablesToUpdate[variable._id].value;
                            }
                        }
                    })
                })
            }
        })
       
    }

    if (data.tags) {
        parsedData.tags = data.tags.map((tag, i) => {
            const docTag = doc.tags[i]

            let value = tag.value;
            if ((tag.type === 'number' || tag.type === 'currency') && Number.isNaN(tag.value)) value = 0;
            value = formatTagValue(tag.type, tag.value);

            return { ...docTag, ...tag, value };
        })
    }

    return {
        ...data,
        ...parsedData
    }
  }
  


export default function useEditorActions() {
    const [state, dispatch] = useEditor()
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const editor = useSlate();
    const { whoami } = useWhoami(state.document);
    const history = useHistory();
    const { tabsMap } = useSidebarMenuOptions();
    const { getValues } = useFormContext();
    const { profile } = useProfileState();

    const onSuccess = (data, v) => {
        console.log('Success', data)
    }

    const onError = (error, variables = {}) => {
        console.log({ error })
        if (!variables.preventAlert) toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
    }
    const onMutate = (error) => {
        dispatch({ type: SET_IS_FETCHING, isFetching: true });
    }
    const onSettled = (error) => {
        dispatch({ type: SET_IS_FETCHING, isFetching: false });
    }

    const { mutateAsync: mutateDoAsyncAction,  mutate: mutateDoAction } = useMutation(({ documentId, action, ...payload }) => {
        const doAction = state.mode === 'template' ? templateAction : contractAction;

        return fetcher(doAction(documentId, action, payload),)
    }, {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })
    const { mutateAsync: mutateUpdateDocument } = useMutation(({ documentId, _v, ...updatedDocument }) => {
        const payload = { ...updatedDocument };
        let updateAction;
        if (state.mode === 'template') {
            updateAction = updateTemplate;
            payload.templateId = documentId
        } else {
            updateAction = updateContract;
            payload.contractId = documentId
        }

        return fetcher(updateAction(payload),)
    }, {
        onMutate,
        onSuccess,
        onError,
        onSettled: () => dispatch({ type: SET_IS_FETCHING, isFetching: false }),
    })
    const { mutateAsync: mutateSendSpecialApproval } = useMutation((payload) => fetcher(sendSpecialApproval(payload),), {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })
    const { mutateAsync: mutateDelegateGroup } = useMutation((payload) => fetcher(delegateGroup(payload),), {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })
    const { mutateAsync: mutateSendNotification } = useMutation((payload) => fetcher(sendNotificationAction(payload),), {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })
    const { mutateAsync: mutateGrabNegotiation } = useMutation(({ documentId, action, ...payload }) => fetcher(clientContractAction(documentId, action, payload),), {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })
    const { mutate: mutateCompareElements,  } = useMutation((payload) => fetcher(compareElements(payload)), {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })
    const { mutate: mutateCalculateQuestionsVariables,  } = useMutation((templateId) => fetcher(calculateQuestionsVariables(templateId)), {
        onMutate,
        onSuccess,
        onError,
        onSettled,
    })


    const navigateToListing = (notification) => {
        const path = state.isTemplate ? '/templates' : '/contracts';
       
        history.push(state.mode === 'frontdesk' ? '/frontdesk' : `${path}/list`);
    }

    const goToTab = (tabName) => {
        let tab = tabsMap.get(tabName)
        if (!tab) tab = tabsMap.get('configuration');
        setCurrentSidebarTab(tab)
    }

    const checkVobo = async (contract) => {
        if (whoami.someoneApproved()) return bootbox.confirm(t('editor.action.reapprove'));
        return true;
    }

    const saveDocument = async (updatedDocument = {}, { updateElements } = {}) => {
        console.info('Document Saved')
        const formData = parseFormData(state.document, getValues(), profile);
        const editableElementsStatus = ['', 'APPROVED', 'PRE_APPROVED'];
        
        const payload = {
            ...state.document,
            ...formData,
            ...updatedDocument,
            documentId: state.document._id,
        }
        delete payload.__v

        if (state.document.isMigrated && (editableElementsStatus.includes(payload.status) || state.mode === 'template')) {
            payload.elements = state.elements
        }
        console.log({doc: state.document, formData, updatedDocument, payload})

        try {
            onMutate()
            await mutateUpdateDocument(payload, {
                onSuccess: (data) => {
                    if (state.mode === 'template') {
                        mutateCalculateQuestionsVariables(payload.documentId)
                    }
                    const doc = getDocument(data) || data;
                    const oldStatus = state?.originalDocument?.status;
                    const newStatus = doc?.status;
            
                    if (state.mode === 'contract' && oldStatus === 'PRE_APPROVED' && newStatus !== 'PRE_APPROVED') {
                        bootbox.alert(t('editor.action.contract_no_longer_approved'));
                    } 
                    
                }
            });
            updateDocument(payload, { updateElements });
        } catch(err) {
            onError(err)
        } finally {
            onSettled()
        }
       


    }

    const saveElements = async ({ refreshElements = true } = {}) => {
        console.info('Elements Saved')
        const elements = editor.children;
        await saveDocument({ elements }, { updateElements: refreshElements })
    }

    const migrateDocument = () => {
        dispatch({ type: SET_IS_MIGRATING, isMigrating: true })
        const parsedElements = migrateElements(state.document.elements, { contract: state.document }) ?? []
        dispatch({ type: SET_IS_MIGRATING, isMigrating: false })
        console.log({parsedElements})
        Transforms.deselect(editor)
        // Transforms.insertNodes(editor, parsedElements)
        updateDocument({
            elements: parsedElements,
            isMigrated: true,
            migratedAt: new Date()
        }, { updateElements: true })
        // Transforms.select(editor, [0])

        // mutateDoAction({
        //   documentId: state.document._id,
        //   action: 'migratedocument',
        //   elements: parsedElements,
        // }, {
        //   onSuccess: (migratedData) => {
        //       const doc = migratedData.contract || migratedData.template;

        //   },
        //   onSettled: () => {
        //     
        //   }
        // })
    }


    const removeSignature = ({ elementId, participantId, signatureId }) => {
        dispatch({ type: REMOVE_SIGNATURE, elementId, participantId, signatureId })
    }

    const changeElementVersion = ({ element, elementPath, version }) => {
        mutateCompareElements({
            contractId: state.document._id,
            version1: version._id, 
            elementId: element._id
        }, {
            onSuccess: (data) => {
                console.log({data})
                dispatch({ type: CHANGE_ELEMENT_VERSION, compareHTML: data.compare, version, elementPath })
            }
        })
        
    }

    const updateDocument = (updatedDocument, { updateElements } = {}) => {
        console.info('Document updated')
        console.log({updatedDocument})
        dispatch({ type: UPDATE_DOCUMENT, document: parseFormData(state.document, updatedDocument, profile) })
        if (updatedDocument.participants) dispatch({ type: SET_PARTICIPANTS, participants: updatedDocument.participants })
        if (updateElements && updatedDocument.elements) dispatch({ type: UPDATE_ELEMENTS, elements: updatedDocument.elements })
    }

    const setCurrentElement = ({ element, location }) => {
        dispatch({ type: SET_CURRENT_ELEMENT, element, location })
    }

    const setCurrentSidebarTab = (tab) => {

        dispatch({ type: SET_CURRENT_SIDEBAR_TAB, tab })
    }

    const addVariable = async ({ participantId, variable }) => {
        
        const defaultVariable = {
            _id: getMongoObjectId(),
            name: t('editor.variables.new_name'),
            apiName: '',
            type: 'text',
            value: '',
            options: [],
        }

        dispatch({ 
                type: ADD_VARIABLE, participantId, variable:  {
                ...defaultVariable,
                ...variable
            } 
        })
    }

    const updateVariable = ({ participantId, variable }, options) => {
        const { name, value, apiName, type } = variable;
        const { participants } = state.document

        const participant = participants.find(p => p._id === participantId)

        if (!participant) return onError({ message: 'ParticipantNotFound' })

        const variableFound = participant.variables.find(v => v._id === variable._id)

        if (!variableFound) return onError({ message: 'VariableNotFound' })

        const variableExists = participant.variables.find((v) => v.apiName === variable.apiName);

        if (variableExists && variableExists._id !== variable._id) return onError({ message: 'VariableExists' })

        // updateEditorVariable(editor, variable);
        dispatch({ type: UPDATE_VARIABLE, participantId, variable })
    }

    const removeVariable = ({ participantId, variable }) => {
        removeNodesById(editor, variable._id)
        dispatch({ type: REMOVE_VARIABLE, participantId, variableId: variable._id })
    }

    const addParticipant = async (participant) => {
        const defaultParticipant = {
            _id: getMongoObjectId(),
            label: t('editor.config.new_participant'),
            name: '',
            obligation: 'individual',
            representative: '',
            phone: '',
            signOrder: state.document.participants.length,
            variables: [],
            signatures: [],
            attachments: []
        };

        const newParticipant = {
            ...defaultParticipant,
            ...participant
        }


        dispatch({ type: ADD_PARTICIPANT, participant: newParticipant });
        _addSignatureToSignatureNodes({ participantId: newParticipant._id });

        // await mutateDoAction({
        //     documentId: state.document._id,
        //     action: 'addparticipant',
        //     participantId,
        //     participant: newParticipant
        // }, {
        //     onSuccess: (data) => {
        //         updateDocument(getDocument(data))
        //         onSuccess(data)
        //         // TODO: add signatures from editor
        //         _addSignatureToSignatureNodes({ participantId: newParticipant._id })
        //     }

        // })
    }

    const updateParticipant = ({ participantId, ...participant }, options) => {
        dispatch({ type: UPDATE_PARTICIPANT, participantId, participant });
    }

    const setIsSidebarOpen = (value) => {
        dispatch({ type: SET_SIDEBAR_OPEN, value });
    }

    const removeParticipant = ({ participantId }) => {
        dispatch({ type: REMOVE_PARTICIPANT, participantId });
        _removeAllSignatureNodes({ participantId });
        removeNodesByProperty(editor, { property: 'participantId', value: participantId })
    }

    const updateSignature = ({ participantId, signature }, options) => {
        dispatch({ type: UPDATE_SIGNATURE, participantId, signature })
    }

    const addSignature = ({ participantId, page }, options) => {
        const defaultSignature = {
            _id: getMongoObjectId(),
            width: 200,
            height: 100,
            top: 0,
            left: 0,
            page
        }
        dispatch({ type: ADD_SIGNATURE, participantId, signature: defaultSignature })
    }

    const removeElement = ({ elementId }) => {
        removeNodesById(editor, elementId)      
    }

    const finalizeDocument = async () => {
        const resp = await bootbox.confirm(t('editor.action.word_finalize'));

        if (!resp) return;

        mutateDoAction({
            documentId: state.document._id,
            action: 'finalize',
        }, {
            onSuccess: (data) => {
                updateDocument(getDocument(data))
            }

        })
    }

    const changePdf = async ({ url, filename }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'registernewversion',
            filename,
            url,
        }, {
            onSuccess: (data) => {
                updateDocument(getDocument(data))
                options?.onSuccess?.(data)
            }

        })
    }

    const _removeAllSignatureNodes = ({ participantId }) => {
        const signatureNodes = [
            ...Editor.nodes(editor, {
                mode: 'highest',
                match: (n) => {
                    const participants = n.participants;
                    return !!participants && participants.includes(participantId);
                },
                at: [],
            }),
        ]

        signatureNodes.map(([node, nodeLocation]) => {
            Transforms.setNodes(editor, {
                participants: node.participants.filter(p => p !== participantId)
            }, {
                at: nodeLocation
            })

        })
    }

    const _addSignatureToSignatureNodes = ({ participantId }) => {
        const signatureNodes = [
            ...Editor.nodes(editor, {
                mode: 'highest',
                match: (n) => {
                    return n.type === 'signature';
                },
                at: [],
            }),
        ]
        console.log({ signatureNodes, participantId })
        signatureNodes.map(([node, nodeLocation]) => {
            Transforms.setNodes(editor, {
                participants: [...node.participants, participantId]
            }, {
                at: nodeLocation
            })
        })
    }

    const nextPage = () => {
        dispatch({ type: NEXT_PAGE })
    }

    const prevPage = () => {
        dispatch({ type: PREV_PAGE })
    }

    const setTotalPages = (totalPages) => {
        dispatch({ type: SET_TOTAL_PAGES, totalPages })
    }

    const setCurrentPage = (page) => {
        dispatch({ type: SET_CURRENT_PAGE, page })
    }

    const addParticipantAttachment = ({participantId, ...newAttachment}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'addattachment',
            participantId,
            ...newAttachment,
        }, {
            onSuccess: (data) => {
                console.log({ data })
                const doc = getDocument(data);
                dispatch({type: UPDATE_PARTICIPANT, participantId,  participant: {
                    attachments: doc.participants.find(p => p._id === participantId)?.attachments || []
                }})
                options?.onSuccess?.(data)
            }
        })
    }

    const updateParticipantAttachment = ({ participantId, attachment }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'updateattachment',
            participantId,
            attachment,
        }, {
            onSuccess: (data) => {
                 const doc = getDocument(data);
                dispatch({type: UPDATE_PARTICIPANT, participantId,  participant: {
                    attachments: doc.participants.find(p => p._id === participantId)?.attachments || []
                }})
                options?.onSuccess?.(data)
            }
        })
    }

    const removeParticipantAttachment = ({ participantId, attachmentId }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'removeattachment',
            participantId,
            attachmentId,
        }, {
            onSuccess: (data) => {
                const doc = getDocument(data);
                dispatch({type: UPDATE_PARTICIPANT, participantId,  participant: {
                    attachments: doc.participants.find(p => p._id === participantId)?.attachments || []
                }})
                options?.onSuccess?.(data)
            }
        })
    }

    const quickVobo = ({ status, approveContract, ...rest }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'quickvobo',
            status,
            approveContract,
            ...rest
        }, {
            onSuccess: (data) => {
                // updateDocument(getDocument(data))
                queryClient.invalidateQueries(['document', state.document._id])
                options?.onSuccess?.(data)
                toast.success(t('editor.collaboration.vobo_quick_success'))
                // navigateToListing('editor.collaboration.vobo_quick_success')
            }
        })
    }

    const uploadWordVersion = (data = {}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'uploadwordversion',
            ...data
        }, {
            onSuccess: (data) => {
                updateDocument(getDocument(data))
                options?.onSuccess?.(data)
            }
        })
    }

    const giveVobo = ({ status, reason, ...data }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'givevobo',
            dateEndVoBo: new Date(),
            status,
            reason,
            ...data
        }, {
            onSuccess: (data) => {
                // updateDocument(getDocument(data))
                options?.onSuccess?.(data)
                toast.success(status === 'rejected' ? t('editor.action.vobo_rejected_success') : t('editor.action.vobo_approved_success'))
                navigateToListing(status === 'rejected' ? 'editor.action.vobo_rejected_success' : 'editor.action.vobo_approved_success')
            }
        })
    }

    const vote = ({ status, ...rest }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'vote',
            status,
            ...rest
        }, {
            onSuccess: (data) => {
                updateDocument(getDocument(data))
                options?.onSuccess?.(data)
                toast.success(t('Voto envíado'))
                navigateToListing('Voto envíado')
            }
        })
    }

    const appproveNegotiation = (data = {}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'approvenegotiation',
            currentVersion: state.document.wordonline.Version,
            ...data
        }, {
            onSuccess: (data) => {
                updateDocument(getDocument(data))
                options?.onSuccess?.(data)
                toast.success(t('editor.action.approved_success'))
                // navigateToListing('editor.action.approved_success')
            }
        })
    }

    const continueWorkflow = (data = {}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'workflowactions', //`/collaboration/contract/${contract.get('_id')}/workflow`;
            workflowAction: 'next',
            ...data
        }, {
            onSuccess: (data) => {
                options?.onSuccess?.(data)
                navigateToListing()
            }
        })
    }

    const approveContractSign = ({ approve, group, ...data }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'approvecontractsign',
            approve,
            group,
            ...data
        }, {
            onSuccess: (data) => {
                options?.onSuccess?.(data)
                toast.success(t('editor.action.send_success'))
                navigateToListing('editor.action.send_success')
            }
        })
    }

    const downloadWord = (data = {}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'downloadword',
            ...data
        }, {
            onSuccess: (data) => {
                options?.onSuccess?.(data)
                downloadFile({ url: data.url, name: `${state.document.documentId}_${state.document.name}`, ext: 'docx' })();
                toast.success(t('editor.action.send_success'))
            }
        })
    }

    const signWordOnline = (data = {}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'converttopdf',
            currentVersion: state.document.wordonline.Version,
            ...data
        }, {
            onSuccess: (data) => {
                options?.onSuccess?.(data)
                updateDocument(getDocument(data))
                toast.success(t('editor.canvas.toolbar.convert_success'))
            }
        })
    }

    const reown = (data = {}, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'reown',
            ...data
        }, {
            onSuccess: (data) => {
                options?.onSuccess?.(data)
                updateDocument(getDocument(data))
                toast.success(t('editor.canvas.toolbar.sent_notification_to_owner'))
            }
        })
    }

    const takeNegotiationTurn = (data = {}, options) => {
        mutateGrabNegotiation({
            documentId: state.document._id,
            action: 'grabnegotiation',
            ...data
        }, {
            onSuccess: (data) => {
                options?.onSuccess?.(data)
                updateDocument(getDocument(data))
                toast.success(t('editor.action.grab_negotiation_success'))
            }
        })
    }

    const sendToSign = async ({ action, accept = false, preventSave = false } = {}, options) => {

        // $('.send-btn').addClass('b-disabled');
        // $('[data-action="save"]').addClass('b-disabled');
        if (!preventSave) await saveDocument();

        mutateDoAction({
            documentId: state.document._id,
            action: 'sendsign',
            sendAction: action,
            accept,
            preventAlert: true
        }, {
            onSuccess: async (data) => {
                // TODO CHECK THROW
                if (data.info) {
                    const sendConfirmResponse = await bootbox.confirm(t(`editor.info.${data.info}`));
                    if (!sendConfirmResponse) return;

                    sendToSign({ action, accept: true, preventSave }, options)

                    return;

                }
                toast.success(t('editor.action.send_success'))
                updateDocument(getDocument(data))
                options?.onSuccess?.(data)
                navigateToListing('editor.action.send_success')

            },
            onError: async (error) => {
                const errorType = error.type;
                const acceptOnly = !errorType || ['vobos', 'limits', 'blockchain'].includes(errorType);
                let buttons = acceptOnly ? {} : { ok: { label: t('editor.action.go_to_configuration'), className: 'btn-primary' } };
                buttons = errorType === 'blockchain' ? { ok: { label: t('editor.action.go_to_blockchain'), className: 'btn-primary' } } : buttons;

                await bootbox.alert({
                    text: t(`errors.${error.message}`, { apiName: error.properties.apiName || '' }),
                    buttons, 
                });

                if (!acceptOnly) goToTab(errorType)
                // if (type === 'blockchain') $(`.fa-hand-holding-usd`).trigger('click');
                // if (!acceptOnly) $(`.editor-sidebar-menu li[data-action="${type}"]`).trigger('click');
            }
        })



        // $('.send-btn').removeClass('b-disabled');
        // $('[data-action="save"]').removeClass('b-disabled');


    }

    const sendSpecialValidation = ({ approval, reason }) => {

        mutateSendSpecialApproval({
            contractId: state.document._id,
            approval,
            reason
        }, {
            onSuccess: (data) => {
                mutateDelegateGroup({ contractId: state.document._id }, {
                    onSuccess: (data) => {
                        toast.success(t(`Has ${(approval ? 'aprobado' : 'rechazado')} este documento.`))
                        history.push('/contracts/list');
                    }
                })
            }
        })

    }

    const initWorkflow = async (data = {}, options) => {
        if (state.initWorkflowInProgress) return false;

        if (!whoami.hasVobo()) {
            await saveDocument();
        }
        

        // CONVERT WORDONLINE TO PDF BEFORE INITIALIZING WORKFLOW
        if (state.documentType === 'word') {
            if (await bootbox.confirm(t('editor.wordonline.errors.convert_to_pdf_first_flow'))) {
                signWordOnline()
            }
            return false;
        }

        // SET IN PROGRESS
        dispatch({ type: SET_INIT_WORKFLOW_IN_PROGRESS, isInitWorkflowInProgress: true })

        // SAVE DOCUMENT
        // if (!whoami.hasVobo()) await self.saveDocument();

        // INITIALIZE WORKFLOW
        mutateDoAction({
            documentId: state.document._id,
            action: 'workflowactions', //`/collaboration/contract/${contract.get('_id')}/workflow`;
            workflowAction: 'init',
        }, {
            onSuccess: async (data) => {
                // WORKFLOW NEEDS WARNING CONFIRMATION
                if (data.info) {
                    dispatch({ type: SET_INIT_WORKFLOW_IN_PROGRESS, isInitWorkflowInProgress: false })

                    // WAIT WARNINGS CONFIRMATION
                    const confirmation = await bootbox.confirm(t(`editor.info.${data.info}`));

                    if (confirmation) {
                        // SEND WARNINGS ACCEPTED
                        mutateDoAction({
                            documentId: state.document._id,
                            action: 'workflowactions', //`/collaboration/contract/${contract.get('_id')}/workflow`;
                            workflowAction: 'init',
                            accept: true
                        }, {
                            onSuccess: async (data) => {
                                toast.success(t('workflow.init.success'))
                                navigateToListing('workflow.init.success');
                            },
                            onError: (error) => {
                                console.error(error);
                            }
                        })
                    }
                    return;
                }
                // SUCCESS
                options?.onSuccess?.(data)
                toast.success(t('workflow.init.success'))
                navigateToListing('workflow.init.success');
            },
            onError: () => {
                dispatch({ type: SET_INIT_WORKFLOW_IN_PROGRESS, isInitWorkflowInProgress: false })
            }
        })


    }

    const addNotification = async ({ notification }, { onSuccess = noop } = {}) => {
        const today = new Date();

        const defaultNotification = {
            name: '',
            recipients: [],
            type: 'onetime',
            frequency: 'daily',
            sendTime: '08',
            sendDate: today,
            startDate: today,
            endDate: add(today, { years: 1 }),
            sendDay: '1',
            message: t('editor.notifications.message_placeholder'),
            active: true
        }


        await mutateDoAction({
            documentId: state.document._id,
            action: 'addnotification',
            notification: {
                ...defaultNotification,
                ...notification
            }
        }, {
            onSuccess: (data) => {
                const notifications = getDocument(data).notifications || []
                updateDocument({ notifications })
                onSuccess(data)
            }

        })
    }

    const updateNotification = ({ notification }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'updatenotification',
            notification
        }, {
            onSuccess: (data) => {
                const notifications = getDocument(data).notifications || []
                updateDocument({ notifications })
                options?.onSuccess?.(data)
            }

        })
    }

    const removeNotification = ({ notificationId }) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'removenotification',
            notificationId
        }, {
            onSuccess: (data) => {
                const notifications = getDocument(data).notifications || []
                updateDocument({ notifications })
            }

        })
    }

    const sendNotification = ({ notificationId }) => {
        mutateSendNotification({
            documentId: state.document._id,
            notificationId
        })
    }

    const getWordonlineVariables = (params, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'getwordonlinevariables'
        }, {
            onSuccess: (data) => {
                // updateDocument(getDocument(data))
                queryClient.invalidateQueries(['document', state.document._id])
                options?.onSuccess(data)
            }
        })
    }

    const compareWordOnlineVersion = ({ version1, version2 }, options) => {
        mutateDoAction({
            documentId: state.document._id,
            action: 'comparewordonlineversions',
            version1,
            version2
        }, options)
    }

    const setPreviewOnly = ({ isPreviewOnly }) => {
        dispatch({ type: SET_PREVIEW_ONLY, isPreviewOnly })
    }

    const changeWordPreviewDocument = ({ versionId }) => {
        dispatch({ type: CHANGE_WORD, versionId })

    }

    return {
        saveDocument,
        updateDocument,
        setCurrentElement,
        updateVariable,
        addVariable,
        removeVariable,
        addParticipant,
        updateParticipant,
        removeParticipant,
        saveElements,
        removeElement,
        addSignature,
        updateSignature,
        removeSignature,
        nextPage,
        prevPage,
        setTotalPages,
        setCurrentPage,
        finalizeDocument,
        changePdf,
        setCurrentSidebarTab,
        goToTab,
        addParticipantAttachment,
        updateParticipantAttachment,
        removeParticipantAttachment,
        migrateDocument,
        quickVobo,
        giveVobo,
        vote,
        appproveNegotiation,
        continueWorkflow,
        approveContractSign,
        downloadWord,
        signWordOnline,
        checkVobo,
        reown,
        takeNegotiationTurn,
        uploadWordVersion,
        sendSpecialValidation,
        sendToSign,
        initWorkflow,
        addNotification,
        updateNotification,
        removeNotification,
        sendNotification,
        getWordonlineVariables,
        compareWordOnlineVersion,
        setPreviewOnly,
        changeWordPreviewDocument,
        parseFormData,
        changeElementVersion,
        setIsSidebarOpen
    }
}